import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    forwardRef,
    inject
} from '@angular/core';
import { ProductAddToBasketComponentDef } from '@df/catalog-ui/product/add-to-basket/product-add-to-basket.component.def';
import { IProductConfigurableSelection, IProductDataV2 } from '@df/catalog/product/product';
import { Client } from '@df/core/service/client';
import { DfEventService } from '@df/core/service/df-event';
import { Customer } from '@df/session/api/customer';
import { AttributeLabelPipeService } from '@df/ui/common/attribute-label.pipe';
import { ClickEventDirective } from '@df/ui/common/click-event.directive';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ScrollEvent, ScrollService } from '@df/util/service/scroll.service';
import { skip } from 'rxjs/operators';
import { ActionComponent } from '../../../../ui/api/action.component';
import { ResultComponent } from '../../../../ui/api/result.component';
import { ProductDecorator } from '../../../model/product/product.decorator';
import { NBackInStockModal } from '../../product/back-in-stock/back-in-stock-modal.interface';
import { ProductConfigurableOptionsComponent } from '../../product/configurable-options';
import { ProductConfigurableOptionsSizeComponent } from '../../product/configurable-options/size/product-configurable-options-size.component';
import {
    PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
    PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME,
    ProductEventData
} from '../../product/product.abstract';
import { ProductService } from '../../product/product.service';
import { IParentView } from '../../product/view/layout/product-view-layout.abstract';
import { WishlistStatus } from '../../wishlist/wishlist.interface';
import { InternalProductsService } from './../../product/internal-products.service';

@Component({
    selector: 'add-to-basket',
    templateUrl: './add-to-basket.html',
    styleUrl: './add-to-basket.scss',
    standalone: true,
    imports: [
        ActionComponent,
        ClickEventDirective,
        CommonModule,
        forwardRef(() => ProductConfigurableOptionsComponent),
        forwardRef(() => ProductConfigurableOptionsSizeComponent),
        ResultComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddToBasketComponent extends ProductAddToBasketComponentDef implements OnInit, OnChanges {
    @Input()
    selection?: IProductConfigurableSelection;

    @Input()
    changeText = false;

    @Input()
    useNotifyModal = false;

    @Input()
    showExternal = false;

    @Input()
    parentView?: IParentView | null;

    showOptions = false;
    private _lastScrollPosition = 0;

    elementRef = inject(ElementRef);
    protected scrollService = inject(ScrollService);
    protected dfEventService = inject(DfEventService);
    protected productService = inject(ProductService);

    constructor(
        customer: Customer,
        changeDetectorRef: ChangeDetectorRef,
        protected client: Client,
        protected attributeLabelPipeService: AttributeLabelPipeService,
        protected internalProductsService: InternalProductsService,
        protected dfModalService: DfModalService
    ) {
        super(customer, changeDetectorRef);

        this.subscriptions.add(this.parentView?.detectChanges$?.pipe(skip(1)).subscribe(() => this.detectChanges()));
        this.subscriptions.add(
            this.scrollService.scroll$.pipe(skip(1)).subscribe((event: ScrollEvent) => {
                const diff = Math.abs(event.scrollTop - this._lastScrollPosition);

                if (diff > 20) {
                    this.closeTray();

                    this._lastScrollPosition = event.scrollTop;
                }
            })
        );
    }

    override ngOnInit(): void {
        this.subscriptions.add(this.internalProductsService.update.subscribe(() => this.detectChanges()));

        if (this.selection?.['size'] || this.selection?.['letter'] || this.showExternal) {
            this.showOptions = true;

            this.detectChanges();
        }

        this.subscriptions.add(
            this.dfEventService.success(event => {
                const product = this.product as ProductDecorator<IProductDataV2>;
                // watch for events on product view
                if (this.productService.canTriggerEvent(event, product)) {
                    if (event.id === PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME) {
                        this.onWishlistPrepareEvent(event.data);
                    }
                }
            })
        );

        super.ngOnInit();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selection']) {
            this.detectChanges();
        }
    }

    get hasAttributeSize() {
        if (this.product?.parent?.isConfigurable) {
            return !!this.product.parent.configurableAttributeCodes?.find(i => i === 'size');
        }
    }

    get hasAttributeLetter() {
        if (this.product?.parent?.isConfigurable) {
            return !!this.product.parent.configurableAttributeCodes?.find(i => i === 'letter');
        }
    }

    get altText(): string | undefined {
        if (!this.changeText || this.client.isS || this.client.isM) {
            return undefined;
        }

        const attrCode = this.needSelectAttribute;

        if (attrCode === 'size') {
            const attribute = this.app.resources.attribute.getExisting(attrCode);

            return `Choose ${attribute?.data?.label.toLowerCase()}`;
        }

        return undefined;
    }

    get needSelectAttribute(): string | undefined {
        const selection = this.selection || {};

        return this.product?.parent?.configurableAttributeCodes?.find(i => !selection[i]);
    }

    closeTray(): void {
        if (this.showOptions) {
            this.showOptions = false;

            if (this.selection && (this.client.isS || this.client.isM)) {
                Object.keys(this.selection).forEach(key => {
                    if ((key === 'size' || key === 'letter') && this.selection?.[key]) {
                        delete this.selection[key];
                    }
                });
            }

            this.dfEventService.dispatch({
                id: PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
                data: {
                    product: this.product as ProductDecorator<IProductDataV2>
                },
                action: () => {
                    this.detectChanges();
                }
            });
        }
    }

    override emitSuccess() {
        this.closeTray();

        super.emitSuccess();
    }

    /**
     * Open modal on size S.
     */
    handleClick(): void {
        const missingAttrCode = this.needSelectAttribute;

        const callback = () => this.submit();

        if (missingAttrCode) {
            if (this.showOptions) {
                if (missingAttrCode === 'size') {
                    this.status.error = 'Select your size';
                } else {
                    this.status.error = `Please select ${this.attributeLabelPipeService.execute(missingAttrCode)}`;
                }
            } else {
                this.showOptions = true;
                this.detectChanges();
            }
            return;
        } else {
            if (!this.isSalable) {
                if (this.client.isX || this.client.isL) {
                    this.app.events.broadcast('xnotif-open');
                } else {
                    this.dfModalService.open(undefined, NBackInStockModal.ID, undefined, this.product);
                }
                return;
            }

            callback();
        }
    }

    handleDataChange(data: IProductConfigurableSelection) {
        this.parentView?.handleDataChange(data);

        this.selection = data;

        this.detectChanges();
    }

    protected onWishlistPrepareEvent(data: ProductEventData) {
        if ((this.client.isS || this.client.isM) && data.status === WishlistStatus.BeforeAdd) {
            this.showOptions = true;

            this.detectChanges();
        }
    }
}
